/**
 * The languages the app is available in.
 *
 * Imported by both the main and the renderer process, so it must stay free of Electron and
 * of browser globals.
 */
export const SUPPORTED_LANGUAGES = ['ro', 'en', 'de', 'fr', 'it', 'es', 'nl', 'pt'] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Romanian is the fallback, not English: every account that existed before the app spoke a
 * second language is Romanian, and the API makes the same choice for the same reason.
 */
export const FALLBACK_LANGUAGE: Language = 'ro';

/** What each language calls itself, for the picker in Settings. */
export const LANGUAGE_LABELS: Record<Language, string> = {
  ro: 'Română',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  nl: 'Nederlands',
  pt: 'Português',
};

export function isSupportedLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * BCP-47 tags for date, number and collation formatting.
 *
 * The app's language is a bare "ro"/"en", which Intl accepts but resolves to whatever
 * regional defaults it likes: "en" alone gives US month/day ordering and a dollar sign in
 * front of a euro amount. These pin the region so a date and a price read the way the reader
 * expects them to.
 *
 * Shared because both clients format money and dates off the same eight languages. A second
 * copy of this table is a second set of regional defaults to keep in step, and the symptom —
 * an invoice punctuated one way on the desktop and another on the phone — is the kind nobody
 * reports as a bug.
 */
export const LOCALE_TAGS: Record<Language, string> = {
  ro: 'ro-RO',
  en: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  es: 'es-ES',
  nl: 'nl-NL',
  pt: 'pt-PT',
};

/** The tag to hand Intl, `toLocaleDateString` and `localeCompare` for a given language. */
export function localeTagFor(language: string | null | undefined): string {
  return LOCALE_TAGS[isSupportedLanguage(language) ? language : FALLBACK_LANGUAGE];
}

/**
 * Maps a full locale tag onto one of the supported languages.
 *
 * Takes the primary subtag, so "ro-RO" and "ro" both land on Romanian, and "en-GB" and
 * "en-US" both on English. A tag whose primary subtag is not one of the eight supported
 * languages (a machine set to Japanese, say) falls back to English rather than to Romanian:
 * a user whose machine is not Romanian is far more likely to read English, even though
 * Romanian remains the fallback for a value we cannot interpret at all.
 */
export function resolveOsLanguage(locale: string | null | undefined): Language {
  if (!locale) return FALLBACK_LANGUAGE;

  const primary = locale.toLowerCase().split(/[-_]/)[0];

  if (isSupportedLanguage(primary)) return primary;

  return 'en';
}

/**
 * The interface language to use for a given account language.
 *
 * Dictation and the interface are offered in the same eight languages, so a supported account
 * language is used directly. Anything outside that set falls back to English rather than to
 * Romanian, by the same reasoning as {@link resolveOsLanguage}: someone whose account is set
 * to a language we do not have is far likelier to read English than Romanian.
 *
 * Takes a plain two-letter code rather than importing the dictation language list, because
 * this module is loaded by the main process and must stay free of React and of Electron.
 */
export function uiLanguageFor(culture: string | null | undefined): Language {
  if (!culture) return FALLBACK_LANGUAGE;

  const primary = culture.toLowerCase().split(/[-_]/)[0];

  return isSupportedLanguage(primary) ? primary : 'en';
}
